import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

/**
 * Handles the Emergent-managed Google OAuth return.
 * The auth service lands the user back at `<origin>/admin#session_id=<id>`.
 * App.js detects the fragment during render and mounts this component FIRST
 * (before ProtectedRoute / normal routes) to avoid race conditions.
 */
export const AuthCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signInWithGoogleSession } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // useRef guard (not useState) — set synchronously so StrictMode's
    // double-invoke can't fire the one-time session exchange twice.
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const sessionId = new URLSearchParams(
      (location.hash || "").replace(/^#/, "")
    ).get("session_id");

    if (!sessionId) {
      navigate("/admin/login", { replace: true });
      return;
    }

    // Strip the fragment immediately so it is never bookmarked or shared
    window.history.replaceState(
      null,
      "",
      window.location.pathname + window.location.search
    );

    signInWithGoogleSession(sessionId)
      .then(() => {
        toast.success("Signed in with Google.");
        navigate("/admin", { replace: true });
      })
      .catch((err) => {
        const d = err?.response?.data?.detail;
        const msg =
          typeof d === "string"
            ? d
            : "Google sign-in failed. Please try again.";
        toast.error(msg);
        navigate("/admin/login", { replace: true, state: { error: msg } });
      });
  }, [location.hash, navigate, signInWithGoogleSession]);

  return (
    <div
      className="min-h-screen bg-[var(--bg)] flex flex-col items-center justify-center gap-6"
      data-testid="auth-callback"
    >
      <Loader2 className="w-6 h-6 animate-spin text-[var(--accent)]" strokeWidth={1.6} />
      <p className="overline text-[var(--ink-50)]">Signing you in…</p>
    </div>
  );
};

export default AuthCallback;
